import {
    Box,
    Button,
    createSvgIcon,
    LinearProgress,
    Typography,
    Badge, // 1. Import Badge
} from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import {
    Applications,
    EntityInstanceTypes,
    IN_TRANSIT_TABS,
    mandatoryInTransitSettingsKeys,
    muiTabsHeight,
    RoutePaths,
    URLSearchParamsKeys,
    UserPermissions,
} from '../../../constants/constants'
import MapView from './MapView'
import Settings from './Settings'
import {
    default_Intransit_Tab,
    setShipmentAttributes,
    setShipmentRelations,
    updateSettings,
} from '../../../redux/slices/inTransitSlice'
import {
    ReactElement,
    startTransition,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {
    ISettingsResponse,
    getApplicationSettings,
} from '../../../services/applications'
import { InTransitSettingsType } from './interfaces'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import {
    CustomTabs,
    CustomTab,
    a11yProps,
    CustomTabPanel,
} from '../../Common/MuiTabs'
import Dashboard from '../../Common/Dashboard'
import { fetchAttributes, fetchRelations } from '../../../services/dtServices'
import { useTranslation } from 'react-i18next'
import theme from '../../../theme/theme'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { updateLoader } from '../../../redux/slices/loaderSlice'
import { findEntityById } from '../../../utils/EntityUtils'
import EntityInstance from '../../Entity/EntityInstance'
import { updateSelectedEntity } from '../../../redux/slices/dtStudioSlice'
import { EntityType } from '../../DTStudio/interfaces'
import BusinessRules from '../../DTStudio/BusinessRules'
import AlertsList from '../../Alerts/AlertsList'
import { fetchUnreadAlertCount } from '../../../services/alertServices'
import { buildAlertFilterParams } from '../../../utils/buildAlertFilterParams'
import { usePermissions } from '../../../hooks/usePermissions'

// 3. Create Bell Icon
const BellIcon = createSvgIcon(
    <svg
        width="16"
        height="19"
        viewBox="0 0 16 19"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M13.3333 6.86111C13.3333 12.6667 16 12.1389 16 14.7778C16 14.7778 16 15.8333 14.9333 15.8333H1.06667C0 15.8333 0 14.7778 0 14.7778C0 12.1389 2.66667 12.6667 2.66667 6.86111C2.66667 3.94672 5.0544 1.58333 8 1.58333C10.9456 1.58333 13.3333 3.94672 13.3333 6.86111Z"
            fill="#FFAC33"
        />
        <path
            d="M8 3.16667C8.88366 3.16667 9.6 2.45778 9.6 1.58333C9.6 0.708883 8.88366 0 8 0C7.11634 0 6.4 0.708883 6.4 1.58333C6.4 2.45778 7.11634 3.16667 8 3.16667Z"
            fill="#FFAC33"
        />
        <path
            d="M8 19C8.5658 19 9.10842 18.7776 9.50849 18.3817C9.90857 17.9858 10.1333 17.4488 10.1333 16.8889H5.86667C5.86667 17.4488 6.09143 17.9858 6.49151 18.3817C6.89158 18.7776 7.43421 19 8 19Z"
            fill="#FFAC33"
        />
    </svg>,
    'Bell',
)

const PlusIcon = createSvgIcon(
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="2.5"
        stroke="currentColor"
        className="size-6"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
    </svg>,
    'Plus',
)

const InTransitTabs = (): ReactElement => {
    const { t } = useTranslation()
    const orgSlice = useAppSelector((state) => state.org)
    const navList = useAppSelector((state) => state.nav.navList)
    const { org, loadingPermissions } = orgSlice
    const orgId = org?.id ? org.id : ''
    const { hasPermission } = usePermissions()
    const hasPermissionAccess = useMemo(
        () =>
            hasPermission([
                UserPermissions.ORG_ADMIN,
                UserPermissions.SYSTEM_READER,
            ]),
        [hasPermission],
    )
    const hasRulesTabAccess = useMemo(
        () =>
            hasPermission([
                UserPermissions.USER_ADMIN,
                UserPermissions.SYSTEM_READER,
            ]),
        [hasPermission],
    )
    const hasWriteAccess = useMemo(
        () => hasPermission([UserPermissions.ENTITY_WRITER]),
        [hasPermission],
    )
    const inTransitSlice = useAppSelector((state) => state.inTransit)
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [isLoadingSettings, setIsLoadingSettings] = useState(true)
    const [errorMessage, setErrorMessage] = useState('')

    const [entityData, setEntityData] = useState<{
        templateName: string
        templateId: string
        entityId: string
        orgId: string
    }>({
        templateName: '',
        templateId: '',
        entityId: '',
        orgId: '',
    })
    const [openEntityInstance, setOpenEntityInstance] = useState(false)
    const [isEdit, setIsEdit] = useState<boolean>(false)
    const { inTransitSettings } = inTransitSlice
    const [searchParams, setSearchParams] = useSearchParams()
    const tabIndexParam = searchParams.get(URLSearchParamsKeys.tabIndex)
    const tabIndex = tabIndexParam
        ? Number.parseInt(tabIndexParam)
        : default_Intransit_Tab

    const shipmentEntityTemplateId =
        inTransitSettings?.shipmentEntityTemplateId || ''

    const enableInTransit = inTransitSettings?.enable || 'false'

    // Build bell-count params from the user's persisted alert filter preferences.
    // We read from the user slice (available on login via getUserData) rather than
    // the alerts slice (only hydrated when AlertsList mounts), so the bell count is
    // correct before the user ever opens the Alerts tab.
    // The query is gated on userId so it doesn't fire before user data has loaded
    // (which would cause it to use empty filters and show the total unread count).
    // per Jira: "The Filter 'Alert Status' has no impact on the bell count"
    const userId = useAppSelector((s) => s.user.user?.id)
    const savedPreference = useAppSelector((s) => s.alerts)
    const userLocalization = useAppSelector(
        (s) => s.user.user?.preferences?.localization,
    )
    const userTimeZone =
        userLocalization?.timeZone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone

    const bellParams = useMemo(() => {
        const filters = {
            searchValue: savedPreference?.searchValue ?? '',
            selectedColumn: savedPreference?.selectedColumn ?? null,
            dateRange: savedPreference?.dateRange ?? [null, null],
            severityFilter: savedPreference?.severityFilter ?? [],
            alertTypeFilter: savedPreference?.alertTypeFilter ?? [],
            readStatusFilter: [],
            productFilter: savedPreference?.productFilter ?? [],
            columnFilters: savedPreference?.columnFilters ?? [],
            sortParams: savedPreference?.sortParams ?? {
                orderAttribute: 'time',
                orderBy: 'DESC' as const,
            },
        }
        const params = buildAlertFilterParams(filters, userTimeZone)
        delete params.alertStatus
        // The bell count is always computed over the backend's default recent window
        // (alerts.default-lookback-days, 60) and must NOT follow the date range the user picked for
        // the alerts list — the badge answers "unread alerts recently", a number that should not move
        // as someone browses a different range. Dropping the dates also keeps this request on the
        // lean count path: any alert date filter disqualifies it (entity-service
        // isUnfilteredAlertCount) and forces the list-grade entity sub-query that UBQ-5209 removed
        // from this hot path.
        delete params.alertStartDate
        delete params.alertEndDate
        return params
    }, [savedPreference, userTimeZone])

    // Bell badge count — AlertsList invalidates this query key when alert status changes
    const { data: alertCountData } = useQuery({
        queryKey: [
            'alerts-unread-count-bell',
            shipmentEntityTemplateId,
            orgId,
            bellParams,
        ],
        queryFn: () =>
            fetchUnreadAlertCount(orgId, shipmentEntityTemplateId, bellParams),
        enabled:
            enableInTransit === 'true' &&
            !!shipmentEntityTemplateId &&
            !!orgId &&
            !!userId,
        staleTime: 5000,
    })
    const unreadAlertCount = Number(alertCountData?.filteredCount) || 0

    useEffect(() => {
        if (tabIndex === IN_TRANSIT_TABS.RULES) {
            const entityTemplate: EntityType | undefined = findEntityById(
                navList,
                shipmentEntityTemplateId,
            ).entity
            dispatch(updateSelectedEntity(entityTemplate))
        }
    }, [inTransitSettings, navList, dispatch])

    const handleTabNavigation = (newValue: number) => {
        if (newValue === IN_TRANSIT_TABS.RULES) {
            const entityTemplate: EntityType | undefined = findEntityById(
                navList,
                shipmentEntityTemplateId,
            ).entity
            dispatch(updateSelectedEntity(entityTemplate))
        }

        startTransition(() => {
            setSearchParams({
                [URLSearchParamsKeys.tabIndex]: newValue.toString(),
            })
        })
    }

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        handleTabNavigation(newValue)
    }

    const handleTabClick = (tabValue: number) => {
        if (tabValue === tabIndex) {
            handleTabNavigation(tabValue)
        }
    }

    useEffect(() => {
        const getSettings = async () => {
            setIsLoadingSettings(true)
            const inTransitAppSettingsResponse: ISettingsResponse<InTransitSettingsType> =
                await getApplicationSettings<InTransitSettingsType>(
                    orgId,
                    Applications.inTransit.id,
                )
            const settings = inTransitAppSettingsResponse?.appSettings || {}
            const enableInTransit = settings?.enable === 'true'
            const areAllMandatoryKeysSet = mandatoryInTransitSettingsKeys.every(
                (key) => !!settings[key],
            )
            if (!enableInTransit || !areAllMandatoryKeysSet) {
                if (hasPermissionAccess) {
                    setSearchParams({
                        [URLSearchParamsKeys.tabIndex]:
                            IN_TRANSIT_TABS.SETTINGS.toString(),
                    })
                } else if (enableInTransit && !areAllMandatoryKeysSet) {
                    setErrorMessage(t('Cold Chain Settings are incomplete'))
                } else {
                    navigate(`/`)
                }
            }
            if (enableInTransit && !!settings?.shipmentEntityTemplateId) {
                const [shipmentAttributes, shipmentRelations] =
                    await Promise.all([
                        fetchAttributes(
                            orgId,
                            settings?.shipmentEntityTemplateId,
                        ),
                        fetchRelations(
                            settings?.shipmentEntityTemplateId,
                            orgId,
                        ),
                    ])
                dispatch(setShipmentAttributes(shipmentAttributes))
                dispatch(setShipmentRelations(shipmentRelations))
            }
            if (enableInTransit && areAllMandatoryKeysSet) {
                setErrorMessage('')
            }
            dispatch(updateSettings(settings))
            setIsLoadingSettings(false)
        }

        if (loadingPermissions) {
            return
        }
        getSettings()
    }, [orgId, loadingPermissions, hasPermissionAccess, navigate, dispatch])

    const analyticsDashboardId = inTransitSettings?.analyticsDashboardId
    const areAllMandatoryKeysSet = mandatoryInTransitSettingsKeys.every(
        (key) => !!inTransitSettings[key],
    )
    const queryClient = useQueryClient()

    if (loadingPermissions || isLoadingSettings) {
        return <LinearProgress />
    }

    if (!hasPermissionAccess && tabIndex === IN_TRANSIT_TABS.SETTINGS) {
        return <Navigate to={RoutePaths.ccl} />
    }

    if (!hasRulesTabAccess && tabIndex === IN_TRANSIT_TABS.RULES) {
        return <Navigate to={RoutePaths.ccl} />
    }

    if (errorMessage) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                }}
            >
                <Typography variant="h5" component="h1" color="error">
                    {errorMessage}
                </Typography>
            </Box>
        )
    }

    const shipmentTemplateId = inTransitSettings.shipmentEntityTemplateId
    const shipmentTemplateName =
        findEntityById(navList, shipmentTemplateId).name ?? ''

    const handleCreateShipmentEntity = () => {
        dispatch(updateLoader(true))
        setOpenEntityInstance(true)
        setIsEdit(false)
        setEntityData({
            templateName: shipmentTemplateName,
            templateId: shipmentTemplateId,
            entityId: '',
            orgId: '',
        })
    }

    const handleCloseFields = () => {
        setOpenEntityInstance(false)
        setEntityData({
            templateName: '',
            templateId: '',
            entityId: '',
            orgId: '',
        })
        queryClient.invalidateQueries({
            queryKey: ['entity-instances-table-data'],
        })
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                    minHeight: `calc(${muiTabsHeight} + 8px)`,
                }}
            >
                <CustomTabs
                    value={tabIndex}
                    onChange={handleChange}
                    aria-label={t('basictabs')}
                    TabIndicatorProps={{
                        children: <span />,
                    }}
                    sx={{ borderBottom: 'none' }}
                >
                    <CustomTab
                        label={t('dashboard')}
                        {...a11yProps(IN_TRANSIT_TABS.DASHBOARD)}
                        disabled={
                            enableInTransit === 'false' || !analyticsDashboardId
                        }
                    />
                    <CustomTab
                        label={t('shipments')}
                        {...a11yProps(IN_TRANSIT_TABS.MAP_VIEW)}
                        disabled={
                            enableInTransit === 'false' ||
                            !areAllMandatoryKeysSet
                        }
                        onClick={() => handleTabClick(IN_TRANSIT_TABS.MAP_VIEW)}
                    />

                    {hasRulesTabAccess && (
                        <CustomTab
                            label={t('rules')}
                            {...a11yProps(IN_TRANSIT_TABS.RULES)}
                            disabled={
                                enableInTransit === 'false' ||
                                !areAllMandatoryKeysSet
                            }
                        />
                    )}
                    <CustomTab
                        value={IN_TRANSIT_TABS.ALERTS}
                        label={
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    paddingRight: unreadAlertCount
                                        ? unreadAlertCount.toString().length ===
                                          4
                                            ? '25px'
                                            : unreadAlertCount.toString()
                                                    .length === 3
                                              ? '22px'
                                              : '16px'
                                        : '0px',
                                }}
                            >
                                {t('alerts')}
                                <Badge
                                    id="intransit-alerts-tab-badge"
                                    badgeContent={
                                        unreadAlertCount
                                            ? `${unreadAlertCount}`
                                            : ''
                                    }
                                    max={999}
                                    sx={{
                                        color: 'red',
                                        fontWeight: 'normal',
                                        '& .MuiBadge-badge': unreadAlertCount
                                            ? {
                                                  transform:
                                                      'scale(1) translate(53%, -70%)',
                                                  background: 'none',
                                                  padding: '2px',
                                                  left:
                                                      unreadAlertCount >= 100
                                                          ? '4px'
                                                          : '',
                                              }
                                            : {},
                                    }}
                                >
                                    <BellIcon fontSize="small" />
                                </Badge>
                            </Box>
                        }
                        {...a11yProps(IN_TRANSIT_TABS.ALERTS)}
                        disabled={
                            enableInTransit === 'false' ||
                            !areAllMandatoryKeysSet
                        }
                    />
                    {hasPermissionAccess && (
                        <CustomTab
                            label={t('settings')}
                            {...a11yProps(IN_TRANSIT_TABS.SETTINGS)}
                        />
                    )}
                </CustomTabs>

                {enableInTransit === 'true' &&
                areAllMandatoryKeysSet &&
                hasWriteAccess ? (
                    <Button
                        sx={{
                            mr: 1,
                            textTransform: 'none',
                            font: theme.typography.button,
                            marginLeft: '20px',
                            backgroundColor: theme.palette.primary.main,
                        }}
                        variant="contained"
                        startIcon={<PlusIcon color="inherit" />}
                        onClick={handleCreateShipmentEntity}
                        id="create-new-shipment-button"
                    >
                        {t('newShipment')}
                    </Button>
                ) : null}
            </Box>

            <CustomTabPanel value={tabIndex} index={IN_TRANSIT_TABS.DASHBOARD}>
                <Dashboard analyticsDashboardId={analyticsDashboardId} />
            </CustomTabPanel>
            <CustomTabPanel value={tabIndex} index={IN_TRANSIT_TABS.MAP_VIEW}>
                <MapView />
            </CustomTabPanel>
            <CustomTabPanel value={tabIndex} index={IN_TRANSIT_TABS.RULES}>
                <BusinessRules maxHeight="calc(100vh - 220px)" />
            </CustomTabPanel>
            <CustomTabPanel value={tabIndex} index={IN_TRANSIT_TABS.SETTINGS}>
                <Settings hasWriteAccess={hasWriteAccess} />
            </CustomTabPanel>
            <CustomTabPanel value={tabIndex} index={IN_TRANSIT_TABS.ALERTS}>
                <AlertsList
                    templateId={inTransitSettings.shipmentEntityTemplateId}
                />
            </CustomTabPanel>
            {openEntityInstance && (
                <EntityInstance
                    isEdit={isEdit}
                    handleCloseFields={handleCloseFields}
                    entityData={entityData}
                    instanceType={EntityInstanceTypes.IN_TRANSIT}
                />
            )}
        </>
    )
}

export default InTransitTabs
